import React, { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, AlertCircle, CheckCircle2, Film, Download } from "lucide-react";
import { apiFetch, resolveApiUrl } from "@/lib/jwt-auth-client";

// ── Types (mirror core/src/domains/managed-agent/jobTracker.ts) ───────────

type JobStatus = "submitted" | "running" | "completed" | "failed";

interface JobStatusResponse {
  jobId: string;
  sessionId: string;
  status: JobStatus;
  taskType: string;
  result?: unknown;
  error?: string;
  createdAt: number;
  updatedAt: number;
}

export interface KlingJobResult {
  agentResponse?: string;
  videoUrl?: string;
  thumbnailUrl?: string;
  /** Batch output — index-aligned with the dispatch's batchItems. Entries
   *  without a `videoUrl` are per-video failures. */
  videos?: Array<{ videoUrl?: string; thumbnailUrl?: string; error?: string }>;
}

interface KlingJobCardProps {
  jobId: string;
  /** Multi-video batch — number of videos this single job will produce. */
  itemCount?: number;
  onComplete?: (jobId: string, result: KlingJobResult) => void;
}

// ── Constants ─────────────────────────────────────────────────────────────

const POLL_INTERVAL_MS = 5_000;
const TERMINAL_STATUSES: ReadonlySet<JobStatus> = new Set(["completed", "failed"]);

// ── Helpers ───────────────────────────────────────────────────────────────

function isTerminal(status: JobStatus): boolean {
  return TERMINAL_STATUSES.has(status);
}

function extractKlingResult(result: unknown): KlingJobResult {
  if (!result || typeof result !== "object") return {};
  const rec = result as Record<string, unknown>;
  const parsed: KlingJobResult = {};

  if (typeof rec.agentResponse === "string" && rec.agentResponse.trim()) {
    parsed.agentResponse = rec.agentResponse.trim();
  }
  if (typeof rec.videoUrl === "string" && rec.videoUrl.trim()) {
    parsed.videoUrl = rec.videoUrl.trim();
  }
  if (typeof rec.thumbnailUrl === "string" && rec.thumbnailUrl.trim()) {
    parsed.thumbnailUrl = rec.thumbnailUrl.trim();
  }
  if (Array.isArray(rec.videos)) {
    const videos: { videoUrl?: string; thumbnailUrl?: string; error?: string }[] = [];
    for (const raw of rec.videos) {
      if (!raw || typeof raw !== "object") continue;
      const v = raw as Record<string, unknown>;
      const entry: { videoUrl?: string; thumbnailUrl?: string; error?: string } = {};
      if (typeof v.videoUrl === "string" && v.videoUrl.trim()) entry.videoUrl = v.videoUrl.trim();
      if (typeof v.thumbnailUrl === "string" && v.thumbnailUrl.trim()) entry.thumbnailUrl = v.thumbnailUrl.trim();
      if (typeof v.error === "string" && v.error.trim()) entry.error = v.error.trim();
      videos.push(entry);
    }
    parsed.videos = videos;
  }

  // Fallback: scan agentResponse for video URLs if structured fields are missing
  if (!parsed.videoUrl && parsed.agentResponse) {
    const videoMatch = parsed.agentResponse.match(
      /https?:\/\/[^\s"'<>)]+\.(?:mp4|webm)(?:\?[^\s"'<>)]*)?/i,
    );
    if (videoMatch) parsed.videoUrl = videoMatch[0];
  }
  if (!parsed.thumbnailUrl && parsed.agentResponse) {
    const thumbMatch = parsed.agentResponse.match(
      /https?:\/\/[^\s"'<>)]+\.(?:jpg|jpeg|png|webp)(?:\?[^\s"'<>)]*)?/i,
    );
    if (thumbMatch) parsed.thumbnailUrl = thumbMatch[0];
  }

  return parsed;
}

function formatElapsed(startMs: number): string {
  const seconds = Math.floor((Date.now() - startMs) / 1_000);
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}m ${secs}s`;
}

// ── Component ─────────────────────────────────────────────────────────────

export function KlingJobCard({ jobId, itemCount, onComplete }: KlingJobCardProps) {
  const [job, setJob] = useState<JobStatusResponse | null>(null);
  const [pollError, setPollError] = useState<string | null>(null);
  const [elapsed, setElapsed] = useState("");
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const mountedRef = useRef(true);
  const startTimeRef = useRef(Date.now());
  const completeFiredRef = useRef(false);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await apiFetch(
        resolveApiUrl(`/core/agent/managed-chat/status/${encodeURIComponent(jobId)}`),
      );
      if (!mountedRef.current) return;
      if (!res.ok) {
        if (res.status === 404) {
          setPollError("Job not found");
        }
        return;
      }
      const data: JobStatusResponse = await res.json();
      setJob(data);
      setPollError(null);
      if (isTerminal(data.status)) {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
      }
    } catch {
      if (!mountedRef.current) return;
      setPollError("Failed to check job status");
    }
  }, [jobId]);

  // Start polling on mount
  useEffect(() => {
    mountedRef.current = true;
    startTimeRef.current = Date.now();

    // Initial fetch
    void fetchStatus();

    // Poll every 5s
    intervalRef.current = setInterval(() => {
      void fetchStatus();
    }, POLL_INTERVAL_MS);

    // Elapsed timer (every second while running)
    timerRef.current = setInterval(() => {
      setElapsed(formatElapsed(startTimeRef.current));
    }, 1_000);

    return () => {
      mountedRef.current = false;
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [fetchStatus]);

  // Fire onComplete once when job reaches completed status
  useEffect(() => {
    if (!job || job.status !== "completed" || completeFiredRef.current) return;
    completeFiredRef.current = true;
    const parsed = extractKlingResult(job.result);
    onComplete?.(jobId, parsed);
  }, [job, jobId, onComplete]);

  const status: JobStatus = job?.status ?? "submitted";

  // ── Failed ──
  if (status === "failed") {
    const errorMsg = job?.error ?? pollError ?? "Video generation failed";
    return (
      <div className="my-2 rounded-2xl bg-surface-low overflow-hidden text-sm border border-red-500/30">
        <div className="px-4 py-3 flex items-center gap-2.5">
          <AlertCircle className="w-4 h-4 text-red-500 shrink-0" />
          <div className="min-w-0">
            <p className="ds-caption text-red-600 dark:text-red-300">
              Video generation failed
            </p>
            <p className="ds-micro text-red-500/80 dark:text-red-400/70 mt-0.5 break-words">
              {errorMsg}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const isBatch = (itemCount ?? 0) > 1;
  const batchLabel = isBatch ? `${itemCount} videos` : "Video";

  // ── Completed ──
  if (status === "completed") {
    const parsed = extractKlingResult(job?.result);
    const videos = parsed.videos && parsed.videos.length > 0
      ? parsed.videos
      : parsed.videoUrl
        ? [{ videoUrl: parsed.videoUrl, thumbnailUrl: parsed.thumbnailUrl }]
        : [];
    const successVideos = videos.filter((v) => v.videoUrl);
    const failureCount = videos.length - successVideos.length;

    return (
      <div className="my-2 rounded-2xl bg-surface-low overflow-hidden text-sm border border-emerald-500/30">
        <div className="px-4 py-3 flex flex-col gap-2">
          <div className="flex items-start gap-2.5">
            <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
            <p className="ds-caption text-emerald-600 dark:text-emerald-300">
              {isBatch
                ? failureCount > 0
                  ? `${successVideos.length} of ${videos.length} videos ready (${failureCount} failed)`
                  : `${successVideos.length} videos ready`
                : "Video generation complete"}
            </p>
          </div>

          {successVideos.map((v, i) => (
            <div
              key={v.videoUrl ?? i}
              className="rounded-lg overflow-hidden bg-black/5 dark:bg-white/5"
            >
              <video
                src={v.videoUrl}
                poster={v.thumbnailUrl}
                controls
                preload="metadata"
                className="w-full max-h-[240px] object-contain"
              />
              {v.videoUrl && (
                <a
                  href={v.videoUrl}
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ds-micro inline-flex items-center gap-1.5 text-primary hover:underline w-fit px-2 py-1"
                >
                  <Download className="w-3 h-3" />
                  Download
                </a>
              )}
            </div>
          ))}

          {successVideos.length === 0 && parsed.agentResponse && (
            <p className="ds-micro text-foreground/70 whitespace-pre-wrap break-words leading-relaxed pl-6.5">
              {parsed.agentResponse}
            </p>
          )}
        </div>
      </div>
    );
  }

  // ── Submitted / Running (in progress) ──
  return (
    <div className="my-2 rounded-2xl bg-surface-low overflow-hidden text-sm border border-border/40">
      <div className="px-4 py-3 flex items-center gap-2.5">
        <div className="relative shrink-0">
          <Film className="w-4 h-4 text-primary/60" />
          <Loader2 className="w-3 h-3 animate-spin text-primary absolute -top-0.5 -right-1" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="ds-caption text-foreground/80">
            {status === "running"
              ? `Generating ${batchLabel.toLowerCase()}…`
              : `${batchLabel} generation queued…`}
          </p>
          <p className="text-[10px] text-foreground/50 mt-0.5">
            {elapsed && `${elapsed} elapsed · `}Job {jobId}
          </p>
        </div>
      </div>
    </div>
  );
}
